import Autocomplete, {
  type AutocompleteProps,
} from "@mui/material/Autocomplete"
import { TextField, TextFieldProps } from "app/components/TextField"
import * as R from "remeda"
import { useState } from "react"

export const Select = ({
  name,
  label,
  options,
  value: valueProps,
  onChange,
  multiple,
  placeholder,
  id,
  errors,
  defaultValue,
  getOptionDisabled,
  isNullable = false,
  description,
  disabled,
  renderTags,
  popupIcon,
  allowSelectAll = false,
}: SelectProps) => {
  const [internalValue, setInternalValue] = useState(valueProps ?? defaultValue)
  const value = valueProps !== undefined ? valueProps : internalValue

  const handleChange: SelectProps["onChange"] = (e, value, reason, details) => {
    const newValue =
      Array.isArray(value) &&
      value.some((option) => option.value === "__SELECT_ALL__")
        ? (options as Option[])
        : value

    onChange?.(e, newValue, reason, details)
    setInternalValue(newValue)
  }

  return (
    <div>
      <Autocomplete
        id={id}
        popupIcon={popupIcon ? popupIcon : undefined}
        sx={
          popupIcon
            ? { "& .MuiAutocomplete-popupIndicator": { transform: "none" } }
            : undefined
        }
        disabled={disabled}
        options={R.pipe(
          [
            multiple && allowSelectAll
              ? {
                  label: "Tout sélectionner",
                  value: "__SELECT_ALL__",
                  preferred: true,
                }
              : null,
            ...options,
          ],
          R.filter(R.isNonNullish),
        )}
        renderValue={renderTags}
        renderInput={(params) => {
          return (
            <TextField
              id={id}
              label={label}
              placeholder={placeholder}
              {...params.slotProps.input}
              inputProps={params.slotProps.htmlInput}
              errors={errors}
              description={description}
              disabled={disabled}
            />
          )
        }}
        onChange={handleChange}
        openOnFocus
        multiple={multiple}
        value={multiple ? (Array.isArray(value) ? value : []) : value || null}
        getOptionDisabled={getOptionDisabled}
        clearIcon={isNullable ? undefined : null}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        disableCloseOnSelect={multiple}
        slotProps={{
          chip: { size: "small" },
        }}
        getOptionKey={(option) => option.value}
        getOptionLabel={(option) => option.label}
      />
      <select
        style={{ display: "none" }}
        name={multiple ? `${name}[]` : name}
        value={
          multiple
            ? Array.isArray(value)
              ? value.map((v) => v.value)
              : []
            : value
              ? Array.isArray(value)
                ? ""
                : value.value
              : ""
        }
        onChange={noop}
        multiple={multiple}
      >
        <option value=""></option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.value}
          </option>
        ))}
      </select>
    </div>
  )
}

export type SelectProps = Pick<
  AutocompleteProps<Option, boolean | undefined, false, false>,
  | "options"
  | "value"
  | "multiple"
  | "onChange"
  | "defaultValue"
  | "getOptionDisabled"
  | "popupIcon"
> &
  Omit<TextFieldProps, "onChange"> & {
    isNullable?: boolean
    allowSelectAll?: boolean
    renderTags?: AutocompleteProps<
      Option,
      boolean | undefined,
      false,
      false
    >["renderValue"]
  }

export interface Option {
  label: string
  value: string
  preferred: boolean
}

const noop = () => {
  // intentionnaly does nothing
}
